import { Pressable } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import { useHaptics, useThemeColors } from '@kiips/ui';
import { colors } from '../../theme/colors';
import {
  annualDiscountPercent,
  type BillingPeriod,
  formatAnnualAsMonthly,
  formatPrice,
  type PlanPrice,
} from '../../lib/pricing-plans';

/**
 * Per-card billing selector wired directly into the price. Renders the
 * monthly and annual SKUs side by side so both numbers are visible at a
 * glance; tapping a segment selects it and drives the card's CTA. Monthly
 * leads (shown first and pre-selected, ADR-0062 amendment 2026-06); the
 * annual segment carries the "Save N%" badge. A subline shows the
 * effective monthly equivalent (annual) or "Billed monthly" — keyed by
 * the period so it cross-fades on change, and single-line so height is
 * stable across the two states.
 *
 * Shared by `/pricing` (the full PlanCard) and the landing-page teaser
 * (`LandingPricingSection`) so the selector reads and behaves identically
 * on both surfaces — one home for the segment markup, badge, and microcopy.
 */
export function PriceSelector({
  price,
  value,
  onChange,
  onDark,
}: {
  price: PlanPrice;
  value: BillingPeriod;
  onChange: (v: BillingPeriod) => void;
  /** True on the highlighted (dark-background) Pro card. */
  onDark: boolean;
}) {
  const theme = useThemeColors();
  return (
    <YStack gap={8}>
      <XStack gap={8}>
        <PriceOption
          active={value === 'monthly'}
          onPress={() => onChange('monthly')}
          label="Monthly"
          priceLabel={formatPrice(price, 'monthly')}
          onDark={onDark}
        />
        <PriceOption
          active={value === 'annual'}
          onPress={() => onChange('annual')}
          label="Annual"
          priceLabel={formatPrice(price, 'annual')}
          badge={`Save ${annualDiscountPercent(price)}%`}
          onDark={onDark}
        />
      </XStack>
      <Text
        key={value}
        transition="quick"
        enterStyle={{ opacity: 0 }}
        opacity={0.7}
        fontSize={12}
        color={onDark ? theme.textInverse : theme.textSubdued}
      >
        {value === 'annual' ? formatAnnualAsMonthly(price) : 'Billed monthly'}
      </Text>
    </YStack>
  );
}

function PriceOption({
  active,
  onPress,
  label,
  priceLabel,
  badge,
  onDark,
}: {
  active: boolean;
  onPress: () => void;
  label: string;
  priceLabel: string;
  badge?: string | undefined;
  onDark: boolean;
}) {
  const theme = useThemeColors();
  const haptics = useHaptics();
  // Unselected border has to read on both the light surface card and the
  // dark highlighted card; the muted theme token vanishes on dark, so use
  // a translucent white there.
  const idleBorder = onDark ? 'rgba(255,255,255,0.22)' : theme.borderMuted;
  return (
    <Pressable
      onPress={() => {
        haptics.selection();
        onPress();
      }}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={`${label} ${priceLabel}${badge ? `, ${badge}` : ''}`}
      style={{ flex: 1 }}
    >
      <YStack
        transition="quick"
        borderWidth={2}
        borderColor={active ? colors.accentCerulean : idleBorder}
        backgroundColor={active ? colors.accentCerulean + '14' : 'transparent'}
        borderRadius={14}
        paddingVertical={10}
        paddingHorizontal={12}
        gap={2}
      >
        <XStack alignItems="center" justifyContent="space-between" gap={6}>
          <Text fontSize={12} fontWeight="700" color={onDark ? theme.textInverse : theme.textSubdued}>
            {label}
          </Text>
          {badge ? (
            <XStack
              backgroundColor={colors.accentCerulean + '22'}
              paddingHorizontal={6}
              paddingVertical={1}
              borderRadius={999}
            >
              <Text fontSize={9} fontWeight="800" color={colors.accentCerulean}>
                {badge}
              </Text>
            </XStack>
          ) : null}
        </XStack>
        <Text fontSize={20} fontWeight="800" color={onDark ? theme.textInverse : theme.textOnSurface}>
          {priceLabel}
        </Text>
      </YStack>
    </Pressable>
  );
}
